import {
  SubscriptionTerm,
  SubscriptionTermLabel,
  SubscriptionTermMonths,
  SubscriptionTermPayLabel,
  SubscriptionTermPeriodText,
} from "./enumUtil"

/**
 * ONE vocabulary for the subscription term on the site. Every `isAnnual ? 12 : 1` /
 * `isAnnual ? annualyPrice : monthlyPrice` / `"/year" : "/month"` is a silent-wrong-answer
 * site for a third term (SemiAnnually), so code reads the term through these helpers only.
 * Ported from the Web app's helpers/plan/subscriptionTermHelper.ts (same names, same rules).
 */

//The paid terms in ascending length
const paidTerms = [
  SubscriptionTerm.Monthly,
  SubscriptionTerm.SemiAnnually,
  SubscriptionTerm.Annually,
]

export function getTermMonths(term) {
  return SubscriptionTermMonths[term || ""] ?? 1
}

export function isPaidTerm(term) {
  return paidTerms.includes(term)
}

//Paid for more than one month at a time (Annually or SemiAnnually)
export function isMultiMonthTerm(term) {
  return getTermMonths(term) > 1
}

//A term that is not one of the paid terms ('' or unknown) is billed like Monthly
export function normalizePaidTerm(term) {
  return isPaidTerm(term) ? term : SubscriptionTerm.Monthly
}

export function getTermLabel(term) {
  return (
    SubscriptionTermLabel[term || ""] ||
    SubscriptionTermLabel[SubscriptionTerm.Monthly]
  )
}

export function getTermPayLabel(term) {
  return SubscriptionTermPayLabel[normalizePaidTerm(term)]
}

export function getTermPeriodText(term) {
  return SubscriptionTermPeriodText[normalizePaidTerm(term)]
}

//"/month", "/6 months", "/year" (no space, as the plan cards print it)
export function getTermPriceSuffix(term) {
  return `/${getTermPeriodText(term)}`
}

function toAmount(value) {
  const amount = typeof value === "string" ? parseFloat(value) : value
  return amount && !isNaN(amount) ? amount : 0
}

//`annualyPrice` is the plan model's spelling, `annuallyPrice` / `annualPrice` other API shapes
export function getPlanAnnualPrice(plan) {
  return toAmount(plan?.annualyPrice ?? plan?.annuallyPrice ?? plan?.annualPrice)
}

//Monthly and Annually are always offered; SemiAnnually only when the admin allowed it and priced it
export function isTermOfferedByPlan(plan, term) {
  if (term === SubscriptionTerm.SemiAnnually) {
    return !!plan?.allowSemiAnnual && toAmount(plan?.semiAnnualPrice) > 0
  }
  return isPaidTerm(term)
}

//The paid terms a plan can be bought on, in ascending length
export function getPaidTermsForPlan(plan) {
  return paidTerms.filter(term => isTermOfferedByPlan(plan, term))
}

//The paid terms at least one of the plans can be bought on (the pricing page pills)
export function getPaidTermsForPlans(plans) {
  return paidTerms.filter(term =>
    term === SubscriptionTerm.SemiAnnually
      ? (plans || []).some(plan => isTermOfferedByPlan(plan, term))
      : true
  )
}

/**
 * Price of one full term of the plan. A term the plan does not offer (semi-annual on a plan
 * that has it switched off) falls back to the monthly price so the screen never shows $0.
 */
export function getPlanPriceForTerm(plan, term) {
  switch (normalizePaidTerm(term)) {
    case SubscriptionTerm.Annually:
      return getPlanAnnualPrice(plan)
    case SubscriptionTerm.SemiAnnually:
      return isTermOfferedByPlan(plan, SubscriptionTerm.SemiAnnually)
        ? toAmount(plan?.semiAnnualPrice)
        : toAmount(plan?.monthlyPrice)
    default:
      return toAmount(plan?.monthlyPrice)
  }
}

/**
 * The term a plan is actually quoted on: '' / unknown bill as Monthly, and a term the plan does
 * not offer (semi-annual on a plan with it switched off) falls back to Monthly so the price and
 * the month multiplier can never disagree. Same rule as the Web app's PlanModel.resolvePlanTerm.
 */
export function resolvePlanTerm(plan, term) {
  const paidTerm = normalizePaidTerm(term)
  return isTermOfferedByPlan(plan, paidTerm) ? paidTerm : SubscriptionTerm.Monthly
}

//What a year of the plan costs when bought on the given term (for "Save $X" comparisons)
export function getAnnualizedPlanPrice(plan, term) {
  const months = getTermMonths(normalizePaidTerm(term))
  return (getPlanPriceForTerm(plan, term) * 12) / months
}

/**
 * What the term saves against paying the same months Monthly (negative = costs more):
 * monthly x the term's months - the term price. Stated per TERM, the same figure as the
 * SAVINGS panel (owner, 2026-09-10: semi-annual must read "Save $500", not the per-year
 * $1,000), and against Monthly so a term's button reads the same whichever term is selected.
 */
export function getTermSaving(plan, term) {
  const paidTerm = normalizePaidTerm(term)
  return (
    getPlanPriceForTerm(plan, SubscriptionTerm.Monthly) * getTermMonths(paidTerm) -
    getPlanPriceForTerm(plan, paidTerm)
  )
}

/**
 * "Save X%" for a multi-month term: how much cheaper the term price is than the same months
 * bought monthly. "" for Monthly, for a term the plan does not offer, and when there is no saving
 * (same result as the old calculateAnnualDiscountPercentage(monthlyPrice, annualyPrice)).
 */
export function getTermDiscountPercentage(plan, term) {
  if (!isMultiMonthTerm(term) || !isTermOfferedByPlan(plan, term)) {
    return ""
  }
  const monthlyPrice = parseFloat(plan?.monthlyPrice)
  const termPrice = getPlanPriceForTerm(plan, term)
  const termPriceBasedOnMonthlyPrice = monthlyPrice * getTermMonths(term)
  if (termPriceBasedOnMonthlyPrice < termPrice) {
    return ""
  }
  const discountPercentage =
    termPriceBasedOnMonthlyPrice === 0
      ? 0
      : ((termPriceBasedOnMonthlyPrice - termPrice) * 100) /
        termPriceBasedOnMonthlyPrice
  //A whole percent, the same figure the pay button prints (owner, 2026-09-21: "27.54%" above and
  //"28%" below were the same saving rounded two ways)
  const wholePercent = Math.round(discountPercentage)
  return wholePercent > 0 ? String(wholePercent) : ""
}

/**
 * date + months on the calendar, with the day clamped to the target month's length (Aug 31 + 6
 * months = Feb 28, not Mar 3). The API's renewal / expiry dates use AddMonths, which clamps the
 * same way; Date.setMonth rolls a 31st past a shorter month, so the pricing page printed a date
 * up to three days after the one the account actually renews on when signing up on the 29th-31st.
 */
export function addMonthsClamped(date, months) {
  const d = new Date(date.getTime())
  const day = d.getDate()
  d.setDate(1)
  d.setMonth(d.getMonth() + months)
  const daysInTarget = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  d.setDate(Math.min(day, daysInTarget))
  return d
}

/**
 * A wall-clock Date as the API's fake-UTC ISO string (the local date/time with a trailing Z). The API
 * stores dates in the user's local time formatted as if UTC; JSON.stringify would shift a Date to real
 * UTC first, moving an evening date to the next day.
 */
export function toApiDate(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString()
}
